import { Suspense, useEffect } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, type Locale } from "@/i18n";
import { Link } from "@/i18n/navigation";
import { NavDropdown } from "@/components/NavDropdown";
import { MobileMenu } from "@/components/MobileMenu";
import { CookieConsent } from "@/components/CookieConsent";
import { SealMark } from "@/components/SealMark";
import {
  platformItems,
  institutionItems,
  developersItems,
  mobileMenuGroups,
} from "@/routes/nav-data";

// Public portal pages that render no portal shell, so they keep the site header.
const PORTAL_PUBLIC_PAGES = new Set(["login", "signup", "accept-invite", "forgot-password", "reset-password"]);

/**
 * True for the signed-in portal (/:locale/portal and its children behind
 * PortalGuard). Those pages bring their own shell (PortalLayout: top bar, menu
 * button, sidebar), so the site header and its spacer stay off them (CE-179).
 */
function isPortalApp(pathname: string): boolean {
  const [, , section, page] = pathname.split("/");
  return section === "portal" && !PORTAL_PUBLIC_PAGES.has(page ?? "");
}

/**
 * /:locale wrapper. Validates the path locale, syncs i18next to it, and renders
 * the real ChainEvidence header/footer (ported from the Next app's
 * src/app/[locale]/layout.tsx, CE-14 P3). Marketing links in the shell full-load
 * to headless Next via the smart @/i18n/navigation Link; app links route
 * client-side. Server-only bits (NextIntlClientProvider, JSON-LD structured
 * data, generateStaticParams) stay on the Next marketing side.
 */
export default function LocaleLayout() {
  const { locale } = useParams();
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const valid = SUPPORTED_LOCALES.includes(locale as Locale);

  useEffect(() => {
    if (valid && locale && i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
  }, [locale, valid, i18n]);

  // Support-ticket chat widget (served by Next at /api/support/widget.js,
  // same-origin). A JSX <script> tag doesn't execute in a client render, so
  // inject it once on mount.
  useEffect(() => {
    if (document.getElementById("ce-support-widget")) return;
    const s = document.createElement("script");
    s.id = "ce-support-widget";
    s.src = "/api/support/widget.js";
    s.defer = true;
    s.dataset.chatApp = "chainevidence";
    s.dataset.theme = "dark";
    document.body.appendChild(s);
  }, []);

  if (!valid) return <Navigate to={`/${DEFAULT_LOCALE}`} replace />;

  return (
    <div className="min-h-screen flex flex-col bg-surface-0">
      {!isPortalApp(pathname) && (
        <>
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 border-b border-edge/50 bg-surface-0/80 backdrop-blur-xl">
          <nav className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <Link href="/" className="flex items-center gap-2 group">
              {/* Logo mark */}
              <span className="text-accent-500"><SealMark size={28} /></span>
              <span className="text-lg font-bold tracking-tight font-display">
                <span className="text-txt-primary">ChainEvidence</span>
              </span>
            </Link>

            {/* Desktop nav — mirrors the Next chrome (CE-57) */}
            <div className="hidden md:flex items-center gap-1">
              <NavDropdown label="Platform" items={platformItems} />
              <NavDropdown label="Institutions" items={institutionItems} />
              <NavDropdown label="Developers" items={developersItems} />
              <Link
                href="/pricing"
                className="px-3 py-2 text-sm font-medium text-txt-secondary hover:text-txt-primary hover:bg-surface-2 rounded-lg transition-all"
              >
                Pricing
              </Link>
              <Link
                href="/trust"
                className="px-3 py-2 text-sm font-medium text-txt-secondary hover:text-txt-primary hover:bg-surface-2 rounded-lg transition-all"
              >
                Trust
              </Link>
              <Link
                href="/portal"
                className="px-3 py-2 text-sm font-medium text-txt-tertiary hover:text-txt-primary transition-colors"
              >
                Sign in
              </Link>
              <Link
                href="/support"
                className="ml-2 px-4 py-2 bg-accent-500 text-surface-0 rounded-lg font-semibold hover:bg-accent-400 transition-colors text-sm"
              >
                Request a briefing
              </Link>
            </div>

            {/* Mobile nav */}
            <MobileMenu groups={mobileMenuGroups} ctaLabel="Request a briefing" ctaHref="/support" />
          </nav>
        </header>

        {/* Spacer for fixed header */}
        <div className="h-16" />
        </>
      )}

      {/* Main content */}
      <main className="flex-1">
        <Suspense
          fallback={
            <div className="px-6 py-24 text-center text-sm text-txt-tertiary">Loading…</div>
          }
        >
          <Outlet />
        </Suspense>
      </main>

      {/* Footer */}
      <footer className="border-t border-edge/50 bg-surface-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-8">
            {/* Brand */}
            <div className="md:col-span-1">
              <Link href="/" className="flex items-center gap-2 mb-4">
                <span className="text-accent-500"><SealMark size={24} /></span>
                <span className="text-sm font-bold font-display">
                  <span className="text-txt-primary">ChainEvidence</span>
                </span>
              </Link>
              <p className="text-xs text-txt-muted leading-relaxed">
                Financial infrastructure for digital-asset compliance. Screening, monitoring, travel rule and compliance reporting for regulated institutions.
              </p>
              <p className="text-xs text-txt-muted/70 mt-2 leading-relaxed">
                Fraud investigation is a separate service. We do not recover funds.
              </p>
            </div>

            {/* Products */}
            <div>
              <h4 className="text-xs font-semibold text-txt-tertiary uppercase tracking-wider mb-4">Platform</h4>
              <div className="space-y-2">
                <Link href="/explore" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Explorer</Link>
                <Link href="/products/api" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">API</Link>
                <Link href="/products/compliance" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Compliance</Link>
                <Link href="/products/monitoring" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Monitoring</Link>
                <Link href="/products/sdk" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">SDK</Link>
                <Link href="/merchant" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Merchant</Link>
                <Link href="/pricing" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Pricing</Link>
              </div>
            </div>

            {/* Tools */}
            <div>
              <h4 className="text-xs font-semibold text-txt-tertiary uppercase tracking-wider mb-4">Tools</h4>
              <div className="space-y-2">
                <Link href="/tools/wallet-profiler" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Wallet Profiler</Link>
                <Link href="/tools/path-finder" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Path Finder</Link>
                <Link href="/tools/tx-decoder" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Tx Decoder</Link>
                <Link href="/tools/scam-detector" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Scam Detector</Link>
              </div>
            </div>

            {/* Developers */}
            <div>
              <h4 className="text-xs font-semibold text-txt-tertiary uppercase tracking-wider mb-4">Developers</h4>
              <div className="space-y-2">
                <Link href="/api-docs" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">API Docs</Link>
                <Link href="/sdk" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">SDK Docs</Link>
                <Link href="/products/sdk#extension" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Browser Extension</Link>
                <Link href="/portal" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Portal</Link>
              </div>
            </div>

            {/* Resources */}
            <div>
              <h4 className="text-xs font-semibold text-txt-tertiary uppercase tracking-wider mb-4">Resources</h4>
              <div className="space-y-2">
                <Link href="/guides" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Guides</Link>
                <Link href="/guides" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Filing System</Link>
                <Link href="/support-resources" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Support</Link>
                <Link href="/support" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Submit Ticket</Link>
                <a href="mailto:support@chainevidence.net" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Contact</a>
              </div>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-xs font-semibold text-txt-tertiary uppercase tracking-wider mb-4">Legal</h4>
              <div className="space-y-2">
                <Link href="/trust" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Trust &amp; Security</Link>
                <Link href="/terms" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Terms</Link>
                <Link href="/privacy" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Privacy</Link>
                <Link href="/impressum" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Impressum</Link>
                <Link href="/refund" className="block text-sm text-txt-secondary hover:text-accent-500 transition-colors">Refund Policy</Link>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-10 pt-6 border-t border-edge/30 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-txt-muted">
              &copy; {new Date().getFullYear()} ChainEvidence. All rights reserved.
            </p>
            <div className="hidden sm:flex items-center gap-2 text-xs text-txt-muted">
              <span>GDPR Compliant</span>
              <span className="text-txt-muted/40">&middot;</span>
              <span>Stripe Secured</span>
              <span className="text-txt-muted/40">&middot;</span>
              <span>Based in EU</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5 text-xs text-txt-muted">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
                All systems operational
              </span>
            </div>
          </div>
        </div>
      </footer>

      <CookieConsent />
    </div>
  );
}
